import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Container,
    Grid,
    Stack,
    Typography,
    TextField,
    InputAdornment,
    FormControl,
    Select,
    MenuItem,
    SelectChangeEvent,
    SxProps,
    Theme,
} from '@mui/material';
import { CoverImageRenderer } from '../../AuditionComponents/CoverImageRenderer';
import RoleRenderer from '../../AuditionComponents/RoleRenderer';
import { theme } from '../../../constants/theme';
import { useThemeContext } from '../../../hook/useThemeManager';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { alpha, styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import {
    BRAND_LUXE_GOLD,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../../../pages/BrandDashboard/brandDashboardTokens';
import { CustomPagination } from '../../Tables/Pagination';
import { IPagination } from '../../../hook/usePagination';
import { IAudition, IRole, IRoleCreator } from '../../../types/global/helper';
import CustomButton from '../../Button/CustomButton';
import { ApplicationCard } from '../../ApplicationCard/ApplicationCard';
import { IApplication } from '../../../types/global/ListRender.types';
import _ from 'lodash';
import close from '../../../asset/images/icons/es_x_close.svg';
import { AuditionDetailsTab } from './Tabs/AuditionDetailsTab';
import { AuditionDetailsEditModal } from './Tabs/AuditionDetailsEditModal';
import { AuditionAnalyticsTab } from './Tabs/AuditionAnalyticsTab';
import { AuditionPromotionsTab } from './Tabs/AuditionPromotionsTab';
import { RoleManagementDialog } from './Dialogs/RoleManagementDialog';
import { DialogueSheet } from '../../DialogueSheet/DialogueSheet';
import { useDispatch } from 'react-redux';
import {
    deleteAudition,
    createRole,
    updateRole,
    publishAudition,
    unPublishAudition,
} from '../../../redux/audition/actions';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormOption } from '../../../types/global/forms.types';
import { CustomizedSwitches } from '../../Switch/Switch';
import { LinkBox } from '../../LinkBox';
import { generateShareLink } from '../../../utils/AppUtils';

// Define Application type that's compatible with IApplicationData
export interface TApplication {
    _id: string;
    user?: {
        profile_pic?: string;
        firstName?: string;
        lastName?: string;
    };
    role?: {
        character?: string;
        _id?: string;
    };
    status?: string;
    createdAt: string;
    total?: number;
    audition?: any;
    filing_status?: string;
    created_at?: string;
    // Add other properties as needed
}

// Styled FormControl for dropdowns
const StyledFormControl = styled(FormControl)(({ theme }) => ({
    minWidth: 120,
    margin: '0 8px',
}));

// Status options
const STATUS_OPTIONS = [
    { value: 'all', label: 'All Status' },
    { value: 'unfiled', label: 'Unfiled' },
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'archived', label: 'Archived' },
    { value: 'invited', label: 'Invited' },
    { value: 'cast', label: 'Cast' },
];

/** Shared height for manage audition filter row (search + selects). */
const MANAGE_AUDITION_FILTER_MIN_HEIGHT = 52;

// Sort options
const SORT_OPTIONS = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'name_asc', label: 'Name (A-Z)' },
    { value: 'name_desc', label: 'Name (Z-A)' },
];

export interface IManageAuditionContainerProps {
    audition: IAudition | null;
    applications: IApplication[];
    loading: boolean;
    error: string | null;
    pagination: IPagination;
    filterStatus: string;
    sortOption: string;
    roleFilter: string;
    onSearchChange: (searchText: string) => void;
    onClearSearch: VoidFunction;
    onFilterStatusChange: (status: string) => void;
    onSortOptionChange: (option: string) => void;
    onRoleFilterChange: (roleId: string) => void;
    onRowsChange: (rows: number) => void;
    onPageChange: (page: number) => void;
    onEditRolesClick: () => void;
    onViewDetailsClick: (application: IApplication) => void;
    onAcceptApplication?: (application: IApplication) => void;
    onRejectApplication?: (application: IApplication) => void;
    onMessageApplicant?: (application: IApplication) => void;
    onArchiveApplication?: (application: IApplication) => void;
    onCastApplicant?: (application: IApplication) => void;
    onImageChange?: (image: string, rawFile: File) => void;
    searchLoading?: boolean;
    /** Default: dashboard sidebar padding. `brand` uses full width inside brand layout outlet. */
    layoutVariant?: 'dashboard' | 'brand';
    /** If set, used after delete instead of `/dashboard` (e.g. brand campaigns list). */
    postDeleteNavigateTo?: string;
    roleDialogPaperSx?: SxProps<Theme>;
    /** Profile drawer stays closed on "View Details"; use with custom navigation (brand participant page). */
    skipApplicationProfileDrawer?: boolean;
    /** When true, cover + inline publish row are omitted (parent renders brand header + hero). */
    omitHeroSection?: boolean;
    /** Optional content rendered right after roles section (before tabs). */
    afterRolesContent?: React.ReactNode;
    /** Parent can open the same details edit modal (e.g. brand page header Edit). */
    onRegisterOpenDetailsEditModal?: (openDetailsEdit: () => void) => void;
    /** Optional styling for the audition details edit dialog (e.g. brand tokens). */
    detailsEditModalPaperSx?: SxProps<Theme>;
}

const ADD_NEW_ROLE_BUTTON_STYLES = {
    borderRadius: 6,
    minWidth: 140,
    padding: '10px 28px',
} as const;

const ManageAuditionContainer: React.FC<IManageAuditionContainerProps> = ({
    audition,
    applications,
    loading,
    error,
    pagination,
    filterStatus,
    sortOption,
    roleFilter,
    onSearchChange,
    onClearSearch,
    onFilterStatusChange,
    onSortOptionChange,
    onRoleFilterChange,
    onRowsChange,
    onPageChange,
    onEditRolesClick,
    onViewDetailsClick,
    onAcceptApplication,
    onRejectApplication,
    onMessageApplicant,
    onArchiveApplication,
    onCastApplicant,
    searchLoading,
    onImageChange,
    layoutVariant = 'dashboard',
    postDeleteNavigateTo,
    roleDialogPaperSx,
    skipApplicationProfileDrawer = false,
    omitHeroSection = false,
    afterRolesContent,
    onRegisterOpenDetailsEditModal,
    detailsEditModalPaperSx,
}) => {
    const { themeStyle } = useThemeContext();
    const filterFieldFill = layoutVariant === 'brand' ? BRAND_SURFACE_CONTAINER_HIGHEST : theme[themeStyle].secondaryBg;
    const [value, setValue] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'applications');
    const [roleDialogOpen, setRoleDialogOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState<IRoleCreator | undefined>(undefined);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
    const [detailsEditModalOpen, setDetailsEditModalOpen] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Create an improved debounced search handler that handles all cases
    const debouncedSearchHandler = useRef(
        _.debounce((searchText: string) => {
            if (onSearchChange) {
                // Simulating a Promise-based onSearchChange operation
                Promise.resolve(onSearchChange(searchText))
                    .catch(error => console.error('Search error:', error))
                    .finally(() => {});
            }
        }, 500),
    ).current;

    // Handle input change with proper debouncing
    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setValue(newValue);
        debouncedSearchHandler(newValue);
    };

    // Handle clear button click
    const handleClearSearch = () => {
        setValue('');
        onClearSearch && onClearSearch();
        // Cancel any pending debounced searches
        debouncedSearchHandler.cancel();
    };

    // Clean up the debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedSearchHandler.cancel();
        };
    }, [debouncedSearchHandler]);

    useEffect(() => {
        onRegisterOpenDetailsEditModal?.(() => setDetailsEditModalOpen(true));
    }, [onRegisterOpenDetailsEditModal]);

    // Extract unique roles for role filter dropdown
    const roleOptions = useMemo(() => {
        if (!audition || !audition.roles) return [];

        const roles = audition.roles
            .filter(role => role !== null)
            .map(role => ({
                id: role._id,
                name: role.character,
            }));

        // Add "All Roles" option
        return [{ id: 'all', name: 'All Roles' }, ...roles];
    }, [audition]);

    // Handle dropdown changes
    const handleStatusChange = (event: SelectChangeEvent<string>) => {
        onFilterStatusChange(event.target.value);
    };

    const handlePublishStatusChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const applicationCount = applications?.length || 0;
            if (event.target.checked) {
                if (applicationCount > 0) {
                    setPublishDialogOpen(true);
                } else {
                    dispatch(publishAudition(audition?._id));
                }
            } else {
                if (applicationCount > 0) {
                    setUnpublishDialogOpen(true);
                } else {
                    dispatch(unPublishAudition(audition?._id));
                }
            }
        },
        [applications?.length, audition?._id, dispatch],
    );

    const renderPublishedStatus = useMemo(() => {
        return (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                {audition && (
                    <LinkBox
                        link={generateShareLink('audition', audition, true)}
                        customComponent={
                            <CustomizedSwitches
                                switchProps={{
                                    checked: audition?.status === 'published',
                                    onChange: handlePublishStatusChange,
                                }}
                                label={audition?.status === 'published' ? 'Published' : 'Draft'}
                                style={{
                                    color: theme[themeStyle].primary_white,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                }}
                            />
                        }
                    />
                )}
            </Box>
        );
    }, [handlePublishStatusChange, themeStyle, audition]);

    const handleSortChange = (event: SelectChangeEvent<string>) => {
        onSortOptionChange(event.target.value);
    };

    const handleRoleChange = (event: SelectChangeEvent<string>) => {
        onRoleFilterChange(event.target.value);
    };

    const tabs = [
        { value: 'applications', label: 'Applications' },
        { value: 'details', label: 'Details' },
        { value: 'analytics', label: 'Analytics' },
        { value: 'promotions', label: 'Promotions' },
    ];

    const handleTabChange = (tabId: string) => {
        setActiveTab(tabId);
        setSearchParams({ tab: tabId });
    };

    const auditionApplicationsFilterSelectSx = useMemo<SxProps<Theme>>(
        () => ({
            minHeight: MANAGE_AUDITION_FILTER_MIN_HEIGHT,
            borderRadius: 9999,
            bgcolor: filterFieldFill,
            color: theme[themeStyle].subtitleGrey,
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                minHeight: MANAGE_AUDITION_FILTER_MIN_HEIGHT,
                py: 0,
                px: 2.5,
                pr: '36px !important',
                boxSizing: 'border-box',
            },
            '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: `1px solid ${theme[themeStyle].primary_orange}`,
            },
            '& .MuiSelect-icon': {
                color: theme[themeStyle].tag_white,
                opacity: 0.9,
                right: 10,
                fontSize: '1.5rem',
            },
        }),
        [filterFieldFill, themeStyle],
    );

    // const handleEditRolesClick = () => {
    //     setRoleDialogOpen(true);
    // };

    const handleRoleSave = (role: Partial<IRole>) => {
        if (selectedRole && selectedRole._id) {
            // Update existing role
            dispatch(
                updateRole({
                    id: selectedRole._id!,
                    body: {
                        ...selectedRole,
                        ...role,
                        role_type: (role.role_type as FormOption) || (selectedRole.role_type as string),
                        is_paid:
                            Number(role.compensation) > 0 || Number(selectedRole.compensation) > 0
                                ? 'paid'
                                : 'unpaid',
                        character: role.character || selectedRole.character,
                        description: role.description || selectedRole.description,
                        gender: role.gender || selectedRole.gender,
                        age_range: [
                            role.min_age ?? selectedRole.min_age,
                            role.max_age ?? selectedRole.max_age,
                        ].join('-'),
                        expertise: role.expertise || selectedRole.expertise,
                        required_media: role.required_media || selectedRole.required_media,
                        currency: role.currency || selectedRole.currency,
                        compensation: role.compensation ?? selectedRole.compensation,
                        requires_subscription:
                            typeof role.requires_subscription === 'boolean'
                                ? role.requires_subscription
                                : selectedRole.requires_subscription,
                        nudity_or_kissing:
                            typeof role.nudity_or_kissing === 'boolean'
                                ? role.nudity_or_kissing
                                : selectedRole.nudity_or_kissing,
                        min_age: role.min_age ?? selectedRole.min_age,
                        max_age: role.max_age ?? selectedRole.max_age,
                    },
                }),
            );
        } else if (audition?._id) {
            // Create new role
            const minAge = role.min_age ?? 0;
            const maxAge = role.max_age ?? minAge;
            const newRole: IRoleCreator = {
                role_type: role.role_type || { label: 'Cast', value: 'cast' },
                is_paid: Number(role.compensation) > 0 ? 'paid' : 'unpaid',
                character: role.character || '',
                description: role.description || '',
                gender: role.gender || '',
                age_range: [minAge, maxAge].join('-'),
                expertise: role.expertise ?? 'beginner',
                required_media: role.required_media || [],
                currency: role.currency || 'USD',
                compensation: role.compensation ?? 0,
                requires_subscription: role.requires_subscription ?? false,
                nudity_or_kissing: role.nudity_or_kissing ?? false,
                min_age: minAge,
                max_age: maxAge,
            };
            dispatch(
                createRole({
                    auditionId: audition._id,
                    body: newRole,
                }),
            );
        }
    };

    const deleteMessage =
        applications?.length > 0
            ? `${audition?.title} has ${applications?.length} applications. Are you sure you want to delete this?`
            : `${audition?.title} is currently live. Are you sure you want to delete this?`;

    const handleDeleteAudition = () => {
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (audition?._id) {
            dispatch(deleteAudition(audition._id));
            navigate(postDeleteNavigateTo ?? '/dashboard');
        }
        setDeleteDialogOpen(false);
    };

    if (!audition && loading && !applications) {
        return (
            <Container disableGutters maxWidth="mdLg" sx={{ py: 4 }}>
                <Typography>Loading...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container
                disableGutters
                sx={{
                    py: 4,
                    height: '100vh',
                    display: 'flex',
                    width: '100vw',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                <Typography color="error">Error loading audition</Typography>
            </Container>
        );
    }

    const containerPaddingSx =
        layoutVariant === 'brand'
            ? {
                  py: 4,
                  px: 0,
                  pr: 0,
                  pl: 0,
                  width: '100%',
              }
            : {
                  py: 4,
                  pr: { xs: 2, sm: 3, md: 4, xl: 3 },
                  pl: { xs: 2, sm: 11, md: 12, xl: '130px' },
                  width: '100%',
              };
    const totalRoles = audition?.roles?.filter(role => role !== null).length ?? 0;
    const rolesAvailabilityLabel =
        totalRoles === 0
            ? 'No roles available'
            : totalRoles === 1
            ? '1 role available'
            : `${totalRoles} roles available`;

    return (
        <Container disableGutters maxWidth={false} sx={containerPaddingSx}>
            {/* Hero Section with Publish Toggle */}
            {!omitHeroSection && (
                <Box sx={{ mb: 4 }}>
                    {audition && (
                        <>
                            <CoverImageRenderer audition={audition} editMode onImageChange={onImageChange} />
                            {renderPublishedStatus}
                        </>
                    )}
                </Box>
            )}

            {/* Roles Section */}
            <Box aria-labelledby="audition-container" sx={{ my: 4 }}>
                <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: { xs: 1.7, sm: 2.25 },
                            py: { xs: 0.82, sm: 0.95 },
                            borderRadius: '12px',
                            background: 'rgba(0, 253, 193, 0.14)',
                            border: '1px solid rgba(0, 253, 193, 0.38)',
                            color: '#00FDC1',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: '0.94rem', sm: '1rem' },
                            letterSpacing: '0.02em',
                            textAlign: 'center',
                            whiteSpace: { xs: 'normal', sm: 'nowrap' },
                            maxWidth: { xs: '100%', sm: 'fit-content' },
                        }}>
                        {rolesAvailabilityLabel}
                    </Box>
                </Stack>

                {audition && (
                    <RoleRenderer
                        audition={audition}
                        isManageMode
                        isBrandMode={layoutVariant === 'brand'}
                        onManageClick={role => {
                            setSelectedRole(role);
                            setRoleDialogOpen(true);
                        }}
                        manageControls={
                            <CustomButton
                                label="Add New Role"
                                onClick={() => {
                                    setSelectedRole(undefined);
                                    setRoleDialogOpen(true);
                                }}
                                withIcon
                                beginningIcon={
                                    <AddRoundedIcon
                                        sx={{
                                            fontSize: 18,
                                            mr: 0.8,
                                            color:
                                                layoutVariant === 'brand' ? '#392700' : theme[themeStyle].button_text,
                                        }}
                                    />
                                }
                                customStyle={ADD_NEW_ROLE_BUTTON_STYLES}
                                sx={{
                                    background:
                                        layoutVariant === 'brand'
                                            ? 'linear-gradient(to top right, #ffc965, #feb700)'
                                            : theme[themeStyle].primary_orange,
                                    backgroundColor: theme[themeStyle].primary_orange,
                                    border: `1px solid ${theme[themeStyle].primary_orange}`,
                                    boxShadow:
                                        layoutVariant === 'brand'
                                            ? '0 0 20px rgba(255,201,101,0.35)'
                                            : `0 0 20px ${alpha(theme[themeStyle].primary_orange, 0.25)}`,
                                    '&:hover': {
                                        background:
                                            layoutVariant === 'brand'
                                                ? 'linear-gradient(to top right, #ffc965, #feb700)'
                                                : theme[themeStyle].primary_orange,
                                        backgroundColor: theme[themeStyle].primary_orange,
                                        filter: layoutVariant === 'brand' ? 'brightness(1.05)' : undefined,
                                        boxShadow:
                                            layoutVariant === 'brand'
                                                ? '0 0 22px rgba(255,201,101,0.35)'
                                                : `0 0 22px ${alpha(theme[themeStyle].primary_orange, 0.35)}`,
                                    },
                                }}
                                textSx={{
                                    color: layoutVariant === 'brand' ? '#392700' : theme[themeStyle].button_text,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    letterSpacing: '0.02em',
                                }}
                            />
                        }
                    />
                )}
            </Box>
            {afterRolesContent}

            {/* Tab Navigation */}
            <Box sx={{ my: 4 }}>
                <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent={'space-between'}>
                    <Box
                        sx={{
                            bgcolor: theme[themeStyle].deep_grey,
                            borderBottom: `1px solid ${theme[themeStyle].wrapperColor}`,
                            mb: 3,
                            width: { xs: 'auto', sm: '100%' },
                        }}>
                        <Box
                            sx={{
                                display: 'flex',
                                width: '100%',
                                overflowX: 'auto',
                                '&::-webkit-scrollbar': { display: 'none' },
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                            }}>
                            {tabs.map(tab => {
                                const isActive = activeTab === tab.value;
                                return (
                                    <Button
                                        key={tab.value}
                                        onClick={() => handleTabChange(tab.value)}
                                        disableRipple
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            py: 2,
                                            px: 1,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            borderRadius: 0,
                                            borderBottom: '2px solid',
                                            borderBottomColor: isActive
                                                ? theme[themeStyle].primary_orange
                                                : 'transparent',
                                            color: isActive
                                                ? theme[themeStyle].primary_orange
                                                : theme[themeStyle].subtitleGrey,
                                            fontFamily: isActive ? FONT_ENUM.MAIN_BOLD : FONT_ENUM.MAIN_MEDIUM,
                                            fontWeight: isActive ? 700 : 600,
                                            fontSize: { xs: '0.9375rem', sm: '1.0625rem' },
                                            lineHeight: 1.25,
                                            textTransform: 'none',
                                            bgcolor: 'transparent',
                                            whiteSpace: 'nowrap',
                                            '&:hover': {
                                                bgcolor: 'transparent',
                                                color: isActive
                                                    ? theme[themeStyle].primary_orange
                                                    : theme[themeStyle].primary_white,
                                            },
                                        }}>
                                        {tab.label}
                                    </Button>
                                );
                            })}
                        </Box>
                    </Box>
                </Stack>

                {activeTab === 'applications' ? (
                    <>
                        {/* Search and Filters */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 2,
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 8,
                            }}>
                            <Box
                                sx={{
                                    flex: '1 1 200px',
                                    minWidth: { xs: '100%', sm: 200 },
                                    position: 'relative',
                                }}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Search applications..."
                                    value={value}
                                    onChange={handleSearchInputChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start" sx={{ ml: 0.5 }}>
                                                <SearchIcon
                                                    sx={{
                                                        color: theme[themeStyle].tag_white,
                                                        opacity: 0.45,
                                                        fontSize: 22,
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                        endAdornment: value ? (
                                            <InputAdornment position="end">
                                                <img
                                                    src={close}
                                                    onClick={handleClearSearch}
                                                    alt="close icon"
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            minHeight: MANAGE_AUDITION_FILTER_MIN_HEIGHT,
                                            borderRadius: 9999,
                                            bgcolor: filterFieldFill,
                                            px: 1.5,
                                            transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                                            '& fieldset': {
                                                border: 'none',
                                            },
                                            '&:hover fieldset': {
                                                border: 'none',
                                            },
                                            '&.Mui-focused fieldset': {
                                                border: `1px solid ${theme[themeStyle].primary_orange}`,
                                            },
                                            '& .MuiInputBase-input': {
                                                color: theme[themeStyle].tag_white,
                                                fontSize: '0.9375rem',
                                                py: 1.25,
                                                pl: 0.5,
                                            },
                                            '& .MuiInputBase-input::placeholder': {
                                                color: theme[themeStyle].subtitleGrey,
                                                opacity: 1,
                                            },
                                        },
                                    }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    flex: '0 1 auto',
                                    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                                }}>
                                <StyledFormControl
                                    variant="outlined"
                                    sx={{
                                        minWidth: 0,
                                        m: 0,
                                        flex: { xs: '1 1 100%', sm: '0 1 auto' },
                                    }}>
                                    <Select
                                        displayEmpty
                                        value={filterStatus}
                                        onChange={handleStatusChange}
                                        variant="outlined"
                                        inputProps={{ 'aria-label': 'Filter by status' }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].gray_text,
                                                    color: theme[themeStyle].primary_white,
                                                    '& .MuiMenuItem-root': {
                                                        color: theme[themeStyle].primary_white,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                            color: theme[themeStyle].black,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].primary_orange,
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                        renderValue={selected => {
                                            const label = STATUS_OPTIONS.find(o => o.value === selected)?.label ?? '';
                                            return `Status: ${label}`;
                                        }}
                                        sx={auditionApplicationsFilterSelectSx}>
                                        {STATUS_OPTIONS.map((option, index) => (
                                            <MenuItem key={index} value={option.value}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        fontSize: '16px',
                                                        color: theme[themeStyle].tag_white,
                                                    }}>
                                                    {option.label}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </StyledFormControl>

                                <StyledFormControl
                                    variant="outlined"
                                    sx={{
                                        minWidth: 0,
                                        m: 0,
                                        flex: { xs: '1 1 100%', sm: '0 1 auto' },
                                    }}>
                                    <Select
                                        displayEmpty
                                        value={roleFilter}
                                        onChange={handleRoleChange}
                                        variant="outlined"
                                        inputProps={{ 'aria-label': 'Filter by role' }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].gray_text,
                                                    color: theme[themeStyle].primary_white,
                                                    '& .MuiMenuItem-root': {
                                                        color: theme[themeStyle].primary_white,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                            color: theme[themeStyle].black,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].primary_orange,
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                        renderValue={selected => {
                                            const name = roleOptions.find(r => r.id === selected)?.name ?? '';
                                            return `Role: ${name}`;
                                        }}
                                        sx={auditionApplicationsFilterSelectSx}>
                                        {roleOptions.map((role, index) => (
                                            <MenuItem key={index} value={role.id}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        fontSize: '16px',
                                                        color: theme[themeStyle].tag_white,
                                                    }}>
                                                    {role.name}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </StyledFormControl>

                                <StyledFormControl
                                    variant="outlined"
                                    sx={{
                                        minWidth: 0,
                                        m: 0,
                                        flex: { xs: '1 1 100%', sm: '0 1 auto' },
                                    }}>
                                    <Select
                                        displayEmpty
                                        value={sortOption}
                                        onChange={handleSortChange}
                                        variant="outlined"
                                        inputProps={{ 'aria-label': 'Sort applications' }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    backgroundColor: theme[themeStyle].gray_text,
                                                    color: theme[themeStyle].primary_white,
                                                    '& .MuiMenuItem-root': {
                                                        color: theme[themeStyle].primary_white,
                                                        '&:hover': {
                                                            backgroundColor: theme[themeStyle].background_gradient.end,
                                                        },
                                                        '&.Mui-selected': {
                                                            backgroundColor: theme[themeStyle].primary_orange,
                                                            color: theme[themeStyle].black,
                                                            '&:hover': {
                                                                backgroundColor: theme[themeStyle].primary_orange,
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                        renderValue={selected => {
                                            const label = SORT_OPTIONS.find(o => o.value === selected)?.label ?? '';
                                            return `Sort: ${label}`;
                                        }}
                                        sx={auditionApplicationsFilterSelectSx}>
                                        {SORT_OPTIONS.map((option, i) => (
                                            <MenuItem key={i} value={option.value}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        fontSize: '16px',
                                                        color: theme[themeStyle].tag_white,
                                                    }}>
                                                    {option.label}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </StyledFormControl>
                            </Box>
                        </Box>

                        {/* Applications Grid */}
                        <Box sx={{ mt: 4, mb: 8, position: 'relative' }}>
                            {searchLoading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'flex-start',
                                        zIndex: 2,
                                        borderRadius: 2,
                                    }}>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: '18px',
                                            mt: 10,
                                        }}>
                                        Searching...
                                    </Typography>
                                </Box>
                            )}
                            <Grid
                                container
                                spacing={3}
                                sx={{
                                    opacity: searchLoading ? 0.4 : 1,
                                    transition: 'opacity 0.2s ease-in-out',
                                }}>
                                {applications && applications.length > 0
                                    ? applications.map(application => (
                                          <Grid item xs={12} sm={6} md={4} key={application._id}>
                                              <ApplicationCard
                                                  application={application}
                                                  skipProfileDrawerOnViewDetails={skipApplicationProfileDrawer}
                                                  onViewDetailsClick={app => onViewDetailsClick(application)}
                                                  onAcceptClick={() =>
                                                      onAcceptApplication && onAcceptApplication(application)
                                                  }
                                                  onRejectClick={() =>
                                                      onRejectApplication && onRejectApplication(application)
                                                  }
                                                  onMessageClick={() =>
                                                      onMessageApplicant && onMessageApplicant(application)
                                                  }
                                                  onArchiveClick={() =>
                                                      onArchiveApplication && onArchiveApplication(application)
                                                  }
                                                  onCastClick={() => onCastApplicant && onCastApplicant(application)}
                                              />
                                          </Grid>
                                      ))
                                    : !searchLoading && (
                                          <Grid item xs={12}>
                                              <Box
                                                  sx={{
                                                      bgcolor:
                                                          layoutVariant === 'brand'
                                                              ? BRAND_SURFACE_CONTAINER_LOW
                                                              : theme[themeStyle].deep_grey,
                                                      borderRadius: 2,
                                                      p: 5,
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      alignItems: 'center',
                                                      gap: 2,
                                                      border:
                                                          layoutVariant === 'brand'
                                                              ? '2px dashed rgba(255,255,255,0.06)'
                                                              : `2px dashed ${alpha(
                                                                    theme[themeStyle].tag_white,
                                                                    0.08,
                                                                )}`,
                                                  }}>
                                                  <Box
                                                      sx={{
                                                          width: 64,
                                                          height: 64,
                                                          borderRadius: 2,
                                                          bgcolor:
                                                              layoutVariant === 'brand'
                                                                  ? BRAND_SURFACE_CONTAINER_HIGHEST
                                                                  : theme[themeStyle].secondaryBg,
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          justifyContent: 'center',
                                                      }}>
                                                      <PeopleOutlineOutlinedIcon
                                                          sx={{
                                                              fontSize: 28,
                                                              color:
                                                                  layoutVariant === 'brand'
                                                                      ? BRAND_LUXE_GOLD
                                                                      : alpha(theme[themeStyle].tag_white, 0.2),
                                                          }}
                                                      />
                                                  </Box>
                                                  <Typography
                                                      sx={{
                                                          fontFamily: FONT_ENUM.MAIN_BOLD,
                                                          fontSize: 15,
                                                          color:
                                                              layoutVariant === 'brand'
                                                                  ? 'rgba(255,255,255,0.4)'
                                                                  : theme[themeStyle].subtitleGrey,
                                                      }}>
                                                      No applications found
                                                  </Typography>
                                                  <Typography
                                                      sx={{
                                                          fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                          fontSize: 13,
                                                          color:
                                                              layoutVariant === 'brand'
                                                                  ? 'rgba(255,255,255,0.25)'
                                                                  : alpha(theme[themeStyle].tag_white, 0.45),
                                                          textAlign: 'center',
                                                          maxWidth: 320,
                                                      }}>
                                                      Applications will appear here when talent submits to your roles.
                                                  </Typography>
                                              </Box>
                                          </Grid>
                                      )}
                            </Grid>
                        </Box>

                        {/* Pagination */}
                        {applications && applications.length > 0 && !searchLoading && (
                            <Box sx={{ mt: 4, mb: 8, display: 'flex', justifyContent: 'center' }}>
                                <CustomPagination
                                    pagination={pagination}
                                    rowsPerPageOptions={[6, 12, 24]}
                                    onRowsChange={onRowsChange}
                                    onPageChange={onPageChange}
                                />
                            </Box>
                        )}
                    </>
                ) : activeTab === 'details' ? (
                    audition && (
                        <AuditionDetailsTab
                            audition={audition}
                            onOpenDetailsEdit={() => setDetailsEditModalOpen(true)}
                        />
                    )
                ) : activeTab === 'analytics' ? (
                    <AuditionAnalyticsTab />
                ) : (
                    <AuditionPromotionsTab />
                )}
            </Box>

            {/* Danger Zone */}
            <Box
                component="section"
                data-purpose="danger-zone-section"
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    mt: 6,
                    p: { xs: 3, md: 5 },
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme[themeStyle].primary_red, 0.3)}`,
                    bgcolor: 'rgba(14,14,14,0.8)',
                    backdropFilter: 'blur(16px)',
                    transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
                    '&:hover': {
                        boxShadow: `0 0 24px ${alpha(theme[themeStyle].primary_red, 0.14)}`,
                        borderColor: alpha(theme[themeStyle].primary_red, 0.4),
                    },
                }}>
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        bgcolor: alpha(theme[themeStyle].primary_red, 0.04),
                        filter: 'blur(72px)',
                        pointerEvents: 'none',
                    }}
                />
                <Grid
                    container
                    spacing={4}
                    alignItems="center"
                    sx={{ position: 'relative', zIndex: 1, gridTemplateColumns: { md: 'repeat(12, 1fr)' } }}>
                    <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '50%',
                                        border: `1px solid ${alpha(theme[themeStyle].primary_red, 0.3)}`,
                                        bgcolor: alpha(theme[themeStyle].primary_red, 0.1),
                                        color: theme[themeStyle].primary_red,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    <ReportProblemOutlinedIcon sx={{ fontSize: 24 }} />
                                </Box>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].primary_red,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: { xs: '1.45rem', md: '1.65rem' },
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.26em',
                                    }}>
                                    Danger Zone
                                </Typography>
                            </Box>

                            <Box sx={{ maxWidth: '42rem' }}>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        fontSize: '0.88rem',
                                        lineHeight: 1.58,
                                    }}>
                                    {applications?.length ? (
                                        <>
                                            This audition has {applications.length}{' '}
                                            {applications.length === 1 ? 'application' : 'applications'}. Deleting it
                                            will remove all associated applications. Once you delete an audition, this
                                            action cannot be undone. All associated applicant data, media, and review
                                            history will be permanently erased.
                                        </>
                                    ) : (
                                        <>
                                            Once you delete an audition, this action cannot be undone. All associated
                                            applicant data, media, and review history will be permanently erased.
                                        </>
                                    )}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].subtitleGrey,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: '0.74rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.12em',
                                        mt: 1.5,
                                    }}>
                                    Please be absolutely certain before proceeding.
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                            <Button
                                fullWidth
                                variant="contained"
                                data-purpose="delete-audition-button"
                                onClick={handleDeleteAudition}
                                sx={{
                                    px: 4,
                                    py: 1.65,
                                    width: { xs: '100%', md: 'auto' },
                                    bgcolor: theme[themeStyle].primary_red,
                                    color: theme[themeStyle].primary_white,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    borderRadius: 2.2,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    boxShadow: `0 10px 15px -3px ${alpha(theme[themeStyle].primary_red, 0.2)}`,
                                    '&:hover': {
                                        bgcolor: alpha(theme[themeStyle].primary_red, 0.85),
                                        boxShadow: `0 10px 15px -3px ${alpha(theme[themeStyle].primary_red, 0.25)}`,
                                    },
                                    '&:focus-visible': {
                                        outline: 'none',
                                        boxShadow: `0 0 0 4px ${alpha(theme[themeStyle].primary_red, 0.35)}`,
                                    },
                                }}>
                                <DeleteForeverOutlinedIcon sx={{ fontSize: 18 }} />
                                Delete Audition
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {audition && (
                <AuditionDetailsEditModal
                    open={detailsEditModalOpen}
                    onClose={() => setDetailsEditModalOpen(false)}
                    audition={audition}
                    visualVariant={layoutVariant}
                    paperSx={detailsEditModalPaperSx}
                />
            )}

            {/* Role Management Dialog */}
            <RoleManagementDialog
                open={roleDialogOpen}
                role={selectedRole}
                onClose={() => {
                    setRoleDialogOpen(false);
                    setSelectedRole(undefined);
                }}
                onSave={handleRoleSave}
                paperSx={roleDialogPaperSx}
            />

            {/* Delete Confirmation Dialog */}
            <DialogueSheet
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onAgreeClicked={handleConfirmDelete}
                onDisagreeClick={() => setDeleteDialogOpen(false)}
                header="Delete Audition"
                body={deleteMessage}
                positiveText="Delete"
                negativeText="Cancel"
                negtiveTextStyle={{
                    color: 'rgba(237, 224, 212, 0.7)',
                    border: `1px solid ${alpha(BRAND_OUTLINE_VARIANT, 0.3)}`,
                    borderRadius: '8px',
                    padding: '10px 24px',
                    background: 'transparent',
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                }}
                positiveTextStyle={{
                    color: theme[themeStyle].primary_white,
                    border: `1px solid ${theme[themeStyle].primary_red}`,
                    borderRadius: '8px',
                    padding: '10px 28px',
                    background: theme[themeStyle].primary_red,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                }}
                paperStyle={{
                    backgroundColor: alpha(BRAND_SURFACE_CONTAINER_LOW, 0.86),
                    border: `1px solid ${alpha(BRAND_OUTLINE_VARIANT, 0.3)}`,
                    boxShadow: '0 24px 40px rgba(0,0,0,0.45)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '12px',
                }}
                headerStyle={{
                    color: '#EDE0D4',
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: '2rem',
                    lineHeight: 1.2,
                    letterSpacing: '-0.02em',
                }}
                bodyStyle={{
                    color: '#D6C4AF',
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    fontSize: '1rem',
                    lineHeight: 1.6,
                }}
                negativeButtonSx={{
                    transition: 'transform 0.2s ease, border-color 0.2s ease, color 0.2s ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        borderColor: alpha(BRAND_OUTLINE_VARIANT, 0.6),
                        color: '#EDE0D4',
                        backgroundColor: 'transparent',
                    },
                }}
                positiveButtonSx={{
                    transition: 'transform 0.2s ease, filter 0.2s ease, box-shadow 0.2s ease',
                    boxShadow: `0 0 28px ${alpha(theme[themeStyle].primary_red, 0.24)}`,
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        filter: 'brightness(1.02)',
                        boxShadow: `0 10px 34px ${alpha(theme[themeStyle].primary_red, 0.42)}`,
                        backgroundColor: theme[themeStyle].primary_red,
                    },
                }}
            />

            {/* Publish Confirmation Dialog */}
            <DialogueSheet
                open={publishDialogOpen}
                onClose={() => setPublishDialogOpen(false)}
                onAgreeClicked={() => {
                    dispatch(publishAudition(audition?._id));
                    setPublishDialogOpen(false);
                }}
                onDisagreeClick={() => setPublishDialogOpen(false)}
                header="Publish Audition"
                body={`This audition has ${
                    applications?.length || 0
                } applications. Are you sure you want to publish it?`}
                positiveText="Publish"
                negativeText="Cancel"
                negtiveTextStyle={{
                    color: theme[themeStyle].tag_white,
                }}
            />

            {/* Unpublish Confirmation Dialog */}
            <DialogueSheet
                open={unpublishDialogOpen}
                onClose={() => setUnpublishDialogOpen(false)}
                onAgreeClicked={() => {
                    dispatch(unPublishAudition(audition?._id));
                    setUnpublishDialogOpen(false);
                }}
                onDisagreeClick={() => setUnpublishDialogOpen(false)}
                header="Unpublish Audition"
                body={`This audition has ${
                    applications?.length || 0
                } applications. Are you sure you want to unpublish it?`}
                positiveText="Unpublish"
                negativeText="Cancel"
                negtiveTextStyle={{
                    color: theme[themeStyle].tag_white,
                }}
            />
        </Container>
    );
};

export { ManageAuditionContainer };
